import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Modal, { Field } from './Modal';
import ErrorState from './ErrorState';
import Loading from './Loading';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { TZ } from '../utils/dates';

/**
 * <MeetingInviteModal /> — invite the client to a site visit, from inside the job.
 *
 * Harvey and Joe called this one of the more valuable calendar features: the
 * client's details are already on the job, so booking the visit with them
 * shouldn't mean re-typing it into Outlook. Everything here is pre-filled from
 * the job's schedule and its property; the send goes out as a real iCalendar
 * REQUEST so the client gets Accept/Decline in their own calendar.
 *
 * A reminder fires automatically the configured number of days beforehand.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// <input type="datetime-local"> wants local wall-clock, not an ISO Z string.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
const fmtRange = (a, b) => {
  if (!a || !b) return '';
  const d = new Date(a);
  const t = (x) => new Date(x).toLocaleTimeString('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  return `${d.toLocaleDateString('en-GB', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short' })}, ${t(a)}–${t(b)}`;
};
const muted = { color: 'var(--cw-text-faint)' };

const ONLINE_OPTIONS = [
  { id: '', label: 'In person' },
  { id: 'teams', label: 'Teams', icon: 'fa-brands fa-microsoft' },
  { id: 'google_meet', label: 'Google Meet', icon: 'fa-brands fa-google' },
  { id: 'zoom', label: 'Zoom', icon: 'fa-solid fa-video' },
];
const ZOOM_RE = /^https:\/\/([a-z0-9-]+\.)*zoom\.(us|com)\/(j|my|w|s)\/\S+$/i;

const MeetingInviteModal = ({ jobId, onClose, onSent }) => {
  // Online meeting: Teams / Meet links are created in the sender's connected calendar;
  // Zoom is a pasted link (no Zoom account connection).
  const [online, setOnline] = useState({ kind: '', join_url: '', external_id: null, creating: false, note: '' });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ title: '', location: '', description: '', start: '', end: '', reminderDays: 7 });
  const [initialForm, setInitialForm] = useState(null);
  const [invitees, setInvitees] = useState([]);          // [{name,email}]
  const [manual, setManual] = useState('');
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const d = await apiFetch(`/api/contractor/jobs/${encodeURIComponent(jobId)}/meeting`);
      setData(d);
      const f = {
        title: d?.defaults?.title || '',
        location: d?.defaults?.location || '',
        description: '',
        start: toLocalInput(d?.defaults?.starts_at),
        end: toLocalInput(d?.defaults?.ends_at),
        reminderDays: d?.defaults?.reminder_days ?? 7,
      };
      setForm(f);
      setInitialForm(f);
      const live = (d?.meetings || []).find((m) => m.status === 'scheduled' && m.join_url);
      if (live) setOnline({ kind: live.online_provider || '', join_url: live.join_url || '', external_id: live.online_external_id || null, creating: false, note: '' });
      // Pre-tick the client's main contact — the common case is "invite the client".
      setInvitees((d?.recipients || []).slice(0, 1).map((r) => ({ name: r.name, email: r.email })));
    } catch (err) {
      setLoadError(err);
    }
    setLoading(false);
  }, [jobId]);
  const dirty = useMemo(() => !!initialForm && (manual.trim() !== ''
    || Object.keys(form).some((k) => String(form[k] ?? '') !== String(initialForm[k] ?? ''))), [form, initialForm, manual]);
  useEffect(() => { load(); }, [load]);

  const has = (email) => invitees.some((i) => i.email.toLowerCase() === String(email).toLowerCase());
  const toggle = (r) => setInvitees((prev) => (has(r.email)
    ? prev.filter((i) => i.email.toLowerCase() !== r.email.toLowerCase())
    : [...prev, { name: r.name, email: r.email }]));
  const addManual = () => {
    const e = manual.trim();
    if (!EMAIL_RE.test(e)) { setErrors((x) => ({ ...x, manual: 'Enter an email address like name@client.co.uk' })); return; }
    setErrors((x) => ({ ...x, manual: undefined, invitees: undefined }));
    if (has(e)) { setManual(''); return; }
    setInvitees((prev) => [...prev, { name: '', email: e }]);
    setManual('');
  };

  const send = async () => {
    const errs = {};
    if (!invitees.length) errs.invitees = 'Add at least one person to invite';
    if (!form.start) errs.start = 'Set a start time';
    if (!form.end) errs.end = 'Set a finish time';
    else if (form.start && new Date(form.end) <= new Date(form.start)) errs.end = 'The finish time must be after the start';
    if (online.kind && !online.join_url) errs.online = online.kind === 'zoom' ? 'Paste the Zoom meeting link' : 'Create the meeting link first, or choose In person';
    else if (online.kind === 'zoom' && !ZOOM_RE.test(online.join_url.trim())) errs.online = 'That doesn’t look like a Zoom meeting link';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      const res = await apiFetch(`/api/contractor/jobs/${encodeURIComponent(jobId)}/meeting`, {
        method: 'POST',
        body: JSON.stringify({
          title: form.title, location: form.location, description: form.description,
          starts_at: fromLocalInput(form.start), ends_at: fromLocalInput(form.end),
          attendees: invitees, reminder_days: Number(form.reminderDays) || 0,
          meeting_id: data?.meetings?.find((m) => m.status === 'scheduled')?.id || undefined,
          ...(online.kind ? { online_provider: online.kind, join_url: online.join_url.trim(), online_external_id: online.external_id || undefined } : {}),
        }),
      });
      const n = res?.sent ?? invitees.length;
      toast.success(`Invitation sent to ${n} ${n === 1 ? 'person' : 'people'}${res?.reminder_days ? ` · reminder ${res.reminder_days} days before` : ''}`);
      if (res?.failed?.length) toast.error(`Couldn’t reach: ${res.failed.join('; ')}`);
      onSent?.(res);
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send the invitation'));
    } finally { setSending(false); }
  };

  const existing = (data?.meetings || []).filter((m) => m.status === 'scheduled');

  const createLink = async () => {
    if (!form.start || !form.end || new Date(form.end) <= new Date(form.start)) { toast.error('Set the start and finish first'); return; }
    setOnline((o) => ({ ...o, creating: true, note: '' }));
    try {
      const r = await apiFetch(`/api/contractor/jobs/${encodeURIComponent(jobId)}/meeting/online`, {
        method: 'POST',
        body: JSON.stringify({ kind: online.kind, title: form.title, starts_at: fromLocalInput(form.start), ends_at: fromLocalInput(form.end), description: form.description }),
      });
      setOnline((o) => ({ ...o, join_url: r?.join_url || '', external_id: r?.external_id || null, creating: false }));
      toast.success(`${online.kind === 'teams' ? 'Teams' : 'Google Meet'} link created`);
    } catch (err) {
      setOnline((o) => ({ ...o, creating: false, note: err?.body?.message || 'Could not create the meeting link' }));
    }
  };

  return (
    <Modal
      title="Invite the client"
      maxWidth={560}
      onClose={onClose}
      dirty={dirty && !sending}
      footer={({ requestClose }) => (<>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose}>Cancel</button>
        <button type="button" className="cw-btn cw-btn-primary" onClick={send} disabled={sending || loading || !!loadError || !data?.mailReady}>
          <i className="fa-solid fa-paper-plane" aria-hidden="true" /> {sending ? 'Sending…' : (existing.length ? 'Send update' : 'Send invitation')}
        </button>
      </>)}
    >
      {loading ? (
        <Loading label="Loading meeting details" />
      ) : loadError ? (
        <ErrorState thing="the meeting details" error={loadError} onRetry={load} compact />
      ) : (
        <>
          {!data?.mailReady && (
            <div role="status" style={{ display: 'flex', gap: 9, padding: '10px 12px', background: 'var(--cw-warning-tint)', border: '1px solid var(--cw-warning-border)', borderRadius: 10, marginBottom: 14, fontSize: 'var(--cs-text-sm)', color: 'var(--cw-warning-fg)' }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginTop: 2 }} aria-hidden="true" />
              <span>No email account is connected — connect one in <strong>Settings → Connect email</strong> before sending invitations.</span>
            </div>
          )}
          {existing.length > 0 && (
            <div style={{ display: 'flex', gap: 9, padding: '10px 12px', background: 'var(--cw-blue-tint)', border: '1px solid var(--cw-border)', borderRadius: 10, marginBottom: 14, fontSize: 'var(--cs-text-sm)', color: 'var(--cw-blue-pressed)' }}>
              <i className="fa-solid fa-circle-info" style={{ marginTop: 2 }} aria-hidden="true" />
              <span>An invitation for <strong>{fmtRange(existing[0].starts_at, existing[0].ends_at)}</strong> has already gone out. Sending again updates that meeting in their calendar rather than creating a second one.</span>
            </div>
          )}

          <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }} aria-describedby={errors.invitees ? 'cw-mi-inv-err' : undefined}>
            <legend className="cw-label">Who to invite <span className="cw-req" aria-hidden="true">*</span></legend>
            {(data?.recipients || []).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
                {data.recipients.map((r) => (
                  <label key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 'var(--cs-text-sm)', cursor: 'pointer', minHeight: 44, flexWrap: 'wrap' }}>
                    <input type="checkbox" checked={has(r.email)} onChange={() => { toggle(r); setErrors((x) => ({ ...x, invitees: undefined })); }} />
                    <span style={{ fontWeight: 600 }}>{r.name}</span>
                    <span style={muted}>{r.email}</span>
                    {r.role && <span className="cw-chip" style={{ background: 'var(--cw-blue-tint)', color: 'var(--cw-blue-pressed)' }}>{r.role}</span>}
                  </label>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 'var(--cs-text-sm)', ...muted, margin: '0 0 8px' }}>
                {data?.clientName
                  ? <>No saved contacts for <strong>{data.clientName}</strong> yet — add an address below (or add contacts on the Clients page).</>
                  : 'No client is set on this property yet — add an address below.'}
              </p>
            )}
            {errors.invitees && <p id="cw-mi-inv-err" className="cw-field-error" role="alert">{errors.invitees}</p>}
          </fieldset>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <Field label="Add someone by email" error={errors.manual} style={{ flex: 1, marginBottom: 0 }}>
              <input type="email" inputMode="email" autoComplete="email" value={manual} placeholder="name@client.co.uk"
                onChange={(e) => { setManual(e.target.value); if (errors.manual) setErrors((x) => ({ ...x, manual: undefined })); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } }} />
            </Field>
            <button type="button" className="cw-btn cw-btn-secondary" onClick={addManual} disabled={!manual.trim()} style={{ marginBottom: errors.manual ? 26 : 0 }}>Add</button>
          </div>
          {invitees.length > 0 && (
            <ul aria-label="Invited" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0 12px', padding: 0, listStyle: 'none' }}>
              {invitees.map((i) => (
                <li key={i.email} className="cw-chip" style={{ background: 'var(--cw-blue-tint)', color: 'var(--cw-blue-pressed)', fontWeight: 600, paddingRight: 2 }}>
                  {i.name || i.email}
                  <button type="button" onClick={() => setInvitees((p) => p.filter((x) => x.email !== i.email))}
                    aria-label={`Remove ${i.name || i.email}`}
                    style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', minWidth: 32, minHeight: 32 }}>
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <Field label="Title"><input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></Field>
          <Field label="Where"><input value={form.location} placeholder="Site address" onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} /></Field>
          <Field label="Online meeting" error={errors.online}>
            <div className="cw-segment" role="radiogroup" aria-label="Online meeting" style={{ flexWrap: 'wrap', marginBottom: 8 }}>
              {ONLINE_OPTIONS.map((o) => (
                <button key={o.id || 'none'} type="button" role="radio" aria-checked={online.kind === o.id} className={online.kind === o.id ? 'active' : ''}
                  onClick={() => { setOnline({ kind: o.id, join_url: '', external_id: null, creating: false, note: '' }); setErrors((x) => ({ ...x, online: undefined })); }}>
                  {o.icon && <i className={o.icon} style={{ marginRight: 5 }} aria-hidden="true" />}{o.label}
                </button>
              ))}
            </div>
            {(online.kind === 'teams' || online.kind === 'google_meet') && (
              online.join_url ? (
                <div className="cw-card" style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--cs-text-sm)', padding: '8px 10px' }}>
                  <i className="fa-solid fa-circle-check" style={{ color: 'var(--cw-success-fg)' }} aria-hidden="true" />
                  <a href={online.join_url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{online.join_url}</a>
                </div>
              ) : (
                <div>
                  <button type="button" className="cw-btn cw-btn-secondary" onClick={createLink} disabled={online.creating}>
                    <i className={`fa-solid ${online.creating ? 'fa-spinner fa-spin' : 'fa-link'}`} aria-hidden="true" /> {online.creating ? 'Creating…' : `Create ${online.kind === 'teams' ? 'Teams' : 'Google Meet'} link`}
                  </button>
                  <div className="cw-meta" style={{ marginTop: 5, color: online.note ? 'var(--cw-warning-fg)' : undefined }}>
                    {online.note || `Made in your connected ${online.kind === 'teams' ? 'Microsoft 365' : 'Google'} calendar. The join link goes in the invitation.`}
                  </div>
                </div>
              )
            )}
            {online.kind === 'zoom' && (
              <>
                <input value={online.join_url} placeholder="https://zoom.us/j/…" inputMode="url"
                  onChange={(e) => { const v = e.target.value; setOnline((o) => ({ ...o, join_url: v })); setErrors((x) => ({ ...x, online: undefined })); }} aria-label="Zoom meeting link" />
                <div className="cw-meta" style={{ marginTop: 5 }}>Create the meeting in Zoom, then paste its link here.</div>
              </>
            )}
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12 }}>
            <Field label="Starts" required error={errors.start}><input type="datetime-local" value={form.start} onChange={(e) => { setForm((f) => ({ ...f, start: e.target.value })); setErrors((x) => ({ ...x, start: undefined })); }} /></Field>
            <Field label="Finishes" required error={errors.end}><input type="datetime-local" value={form.end} min={form.start || undefined} onChange={(e) => { setForm((f) => ({ ...f, end: e.target.value })); setErrors((x) => ({ ...x, end: undefined })); }} /></Field>
          </div>
          <Field label="Reminder">
            <select value={form.reminderDays} onChange={(e) => setForm((f) => ({ ...f, reminderDays: e.target.value }))}>
              <option value={0}>No reminder</option>
              <option value={1}>1 day before</option>
              <option value={3}>3 days before</option>
              <option value={7}>A week before</option>
              <option value={14}>2 weeks before</option>
            </select>
          </Field>
          <Field label="Message (optional)">
            <textarea rows={3} style={{ resize: 'vertical' }} value={form.description}
              placeholder="Anything the client should know — access arrangements, who's attending…"
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </Field>
          <p style={{ fontSize: 'var(--cs-text-xs)', ...muted, margin: 0 }}>
            Sends a calendar invitation they can accept or decline. It lands in their calendar, not just their inbox.
          </p>
        </>
      )}
    </Modal>
  );
};

export default MeetingInviteModal;
